import { FC, useState, useRef } from "react";
import {
    Flex,
    Box,
    Text,
    Tabs,
    TabList,
    Tab,
    TabPanels,
    Picture,
    Button,
    Heading,
} from "@smile2impress/components/relaunch";

import { Stars } from "components/Relaunch/Stars";

import { CustomerReview } from "../primitives/CustomerReview";
import { FourColumnTabPanel } from "./components/FourColumnTabPanel";
import { TwoThreeColumnTabPanel } from "./components/TwoThreeColumnTabPanel";
import { TABBED_CONTENT_TYPE, TabbedContentProps } from "./TabbedContent.types";

export const TabbedContent: FC<TabbedContentProps> = ({
    content: { rating, header, tabs, showMoreButtonLabel, disclaimer },
    type = TABBED_CONTENT_TYPE.FOUR_COLUMN,
}) => {
    const isFourColumn = type === TABBED_CONTENT_TYPE.FOUR_COLUMN;

    const defaultNumberOfVisibleItems = isFourColumn ? 8 : 7;

    const [currentTab, setCurrentTab] = useState(0);
    const [numberOfVisibleItems, setNumberOfVisibleItems] = useState(
        defaultNumberOfVisibleItems
    );

    const scrollRef = useRef<HTMLDivElement | null>(null);
    const tabsRefs = useRef<{ [key: number]: HTMLButtonElement }>({});

    const handleTabChange = (index: number) => {
        setCurrentTab(index);
        setNumberOfVisibleItems(defaultNumberOfVisibleItems);

        const scrollLeft = tabsRefs.current?.[index]?.offsetLeft - 150;

        if (scrollLeft && scrollRef?.current) {
            scrollRef?.current.scrollTo({
                top: 0,
                left: !index ? 0 : scrollLeft,
                behavior: "smooth",
            });
        }
    };

    const handleClickShowMoreButton = () => {
        setNumberOfVisibleItems((prevState) => prevState + 8);
    };

    return (
        <Box>
            {!!rating && (
                <Stars
                    rating={rating}
                    blockProps={{
                        justifyContent: "center",
                        mb: 0.7,
                    }}
                    iconProps={{
                        w: "20px",
                        h: "20px",
                    }}
                />
            )}
            <Flex
                justifyContent="center"
                alignItems="center"
                px={2}
                mb={[3, 3, 4]}
            >
                <Box maxW="800px" textAlign="center">
                    <Heading
                        textStyle="title1"
                        mb={[1, 1, 0.7]}
                        color="text.base"
                    >
                        {header[currentTab].title}
                    </Heading>
                    <Text textStyle="body">
                        {header[currentTab].description}
                    </Text>
                </Box>
            </Flex>
            <Tabs maxW="100%" onChange={handleTabChange}>
                <TabList ref={scrollRef}>
                    {tabs.map((tab, index) => (
                        <Tab
                            key={`tab-${index}`}
                            ref={(element: HTMLButtonElement) =>
                                (tabsRefs.current[index] = element)
                            }
                        >
                            <Text textStyle="body" color="inherit">
                                {tab.title}
                            </Text>
                        </Tab>
                    ))}
                </TabList>
                {!!tabs[currentTab]?.subtitle && (
                    <Text
                        textStyle="H3"
                        textAlign="center"
                        px={2}
                        my={[3, 3, 4]}
                        textTransform="uppercase"
                    >
                        {tabs[currentTab].subtitle}
                    </Text>
                )}
                <TabPanels px={[2, 2, 0]}>
                    {tabs.map((tab, index) => {
                        const tabContent = tab.content.slice(
                            0,
                            numberOfVisibleItems
                        );

                        return isFourColumn ? (
                            <FourColumnTabPanel key={`tab-panel-${index}`}>
                                {tabContent.map((content, index) => (
                                    <Box
                                        key={`tab-panel-content-${index}`}
                                        position="relative"
                                        justifySelf="center"
                                        alignSelf="center"
                                        maxW={["100%", "100%", "246.5px"]}
                                    >
                                        <Picture
                                            imageProps={{
                                                borderRadius: [8, 8, 14],
                                            }}
                                            {...content.image}
                                        />
                                        {!!content?.label && (
                                            <Flex
                                                position="absolute"
                                                bottom={["8px", "8px", 1]}
                                                right={["8px", "8px", 1]}
                                                justifyContent="center"
                                                alignItems="center"
                                                h={["18px", "18px", 2]}
                                                bgColor="base.halfBlack"
                                                p={0.5}
                                                borderRadius={4}
                                            >
                                                <Text
                                                    textStyle="captionSmall"
                                                    color="text.baseInverse"
                                                >
                                                    {content.label}
                                                </Text>
                                            </Flex>
                                        )}
                                    </Box>
                                ))}
                            </FourColumnTabPanel>
                        ) : (
                            <TwoThreeColumnTabPanel key={`tab-panel-${index}`}>
                                {tabContent.map((content, index) => (
                                    <CustomerReview
                                        key={`tab-panel-content-${index}`}
                                        {...content}
                                    />
                                ))}
                            </TwoThreeColumnTabPanel>
                        );
                    })}
                </TabPanels>
            </Tabs>
            {disclaimer && (
                <Box px={[2, 2, 4]} mt={[2, 2, 3]} textAlign="center">
                    <Text textStyle="captionSmall" color="base.halfBlack">
                        {disclaimer}
                    </Text>
                </Box>
            )}
            {numberOfVisibleItems < tabs[currentTab].content.length && (
                <Box px={[2, 2, 4]} mt={[1, 1, 4]}>
                    <Button
                        variant="stroked"
                        w="100%"
                        onClick={handleClickShowMoreButton}
                    >
                        <Text textStyle="body">{showMoreButtonLabel}</Text>
                    </Button>
                </Box>
            )}
        </Box>
    );
};
